/**
 * Error envelope.
 *
 * The original answered every failure with HTTP 200 and a body carrying
 * `code: '400'`, which meant `fetch` callers had to inspect the payload to know
 * whether anything worked. Here the status code is authoritative and the body
 * only adds detail — but `fieldErrors` preserves the per-field messages the
 * forms rely on.
 */

export interface ApiErrorBody {
  error: {
    message: string;
    code: ApiErrorCode;
    /** Field name -> message, for inline form validation. */
    fieldErrors?: Record<string, string>;
  };
}

export const API_ERROR_CODES = [
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'upstream_error',
  'internal_error',
] as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

const STATUS_BY_CODE: Record<ApiErrorCode, number> = {
  bad_request: 400,
  unauthorized: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  rate_limited: 429,
  upstream_error: 502,
  internal_error: 500,
};

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly statusCode: number;
  readonly fieldErrors?: Record<string, string>;

  constructor(code: ApiErrorCode, message: string, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.statusCode = STATUS_BY_CODE[code];
    this.fieldErrors = fieldErrors;
  }

  toBody(): ApiErrorBody {
    return {
      error: {
        message: this.message,
        code: this.code,
        ...(this.fieldErrors ? { fieldErrors: this.fieldErrors } : {}),
      },
    };
  }

  static badRequest(message = 'Bad Request', fieldErrors?: Record<string, string>): ApiError {
    return new ApiError('bad_request', message, fieldErrors);
  }

  static unauthorized(message = 'Authorization required'): ApiError {
    return new ApiError('unauthorized', message);
  }

  static forbidden(message = 'Action not allowed'): ApiError {
    return new ApiError('forbidden', message);
  }

  static notFound(message = 'Not found'): ApiError {
    return new ApiError('not_found', message);
  }

  static conflict(message: string, fieldErrors?: Record<string, string>): ApiError {
    return new ApiError('conflict', message, fieldErrors);
  }

  static upstream(message = 'Upstream service failed'): ApiError {
    return new ApiError('upstream_error', message);
  }
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = (value as ApiErrorBody).error;
  return typeof candidate === 'object' && candidate !== null && typeof candidate.message === 'string';
}
